import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { getSortedList, getUniqueName } from 'utils/stringFormat';
import IntegrationFormOverlay from './IntegrationFormOverlay';
import { IntegrationFormOverlayProps, ManageIntegrationsOverlayProps } from '../types';

const tableHeaderSx = { lineHeight: '1.13', fontSize: '16px', fontWeight: 600 };

type SortColumn = 'name' | 'code';

const ManageIntegrationsOverlay = ({ onClose }: ManageIntegrationsOverlayProps) => {
  const { getIntegrations, createIntegration, updateIntegration, deleteIntegration, resetError } = useActions({
    getIntegrations: integrationsActions.getIntegrations,
    createIntegration: integrationsActions.createIntegration,
    updateIntegration: integrationsActions.updateIntegration,
    deleteIntegration: integrationsActions.deleteIntegration,
    resetError: integrationsActions.resetError
  });
  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const createdIntegration = useSelector(integrationsSelectors.selectCreatedIntegration);
  const { pending, error, errorCode } = useSelector(integrationsSelectors.selectAction);

  // The form is open when this is set: null creates a new integration, otherwise it edits that one
  const [integrationToEdit, setIntegrationToEdit] = useState<Integration | null | undefined>(undefined);
  const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const sortedIntegrations = useMemo(() => {
    // Copies share a code, so ties fall back to the other column
    const sorted = getSortedList(integrations, sortColumn, sortColumn === 'name' ? 'code' : 'name');
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [integrations, sortColumn, sortOrder]);

  const onSortClick = (column: SortColumn) => {
    setSortOrder(sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const renderSortIcon = (column: SortColumn) =>
    sortColumn === column && (
      <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    );

  useEffect(() => {
    getIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh the list once a create, update or delete request finishes. The form stays open on error.
  useEffect(() => {
    if (uiLoading && !pending) {
      // A finished copy opens the new integration for editing
      if (!error) setIntegrationToEdit(isCopying && createdIntegration ? createdIntegration : undefined);
      setIsCopying(false);
      setUILoading(false);
      getIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiLoading, pending]);

  const onFormSubmit: IntegrationFormOverlayProps['onSubmit'] = (payload) => {
    setUILoading(true);
    if (integrationToEdit) {
      updateIntegration({ id: integrationToEdit.id, ...payload });
    } else {
      createIntegration(payload);
    }
  };

  const onCopyClick = (integration: Integration) => {
    setUILoading(true);
    setIsCopying(true);
    createIntegration({ name: getUniqueName(integration.name, integrations), code: integration.code });
  };

  const onDeleteConfirmClick = () => {
    if (integrationToDelete) {
      setUILoading(true);
      deleteIntegration({ id: integrationToDelete.id });
      setIntegrationToDelete(null);
    }
  };

  return (
    <FormDialog
      open
      title="Integrations"
      cancelButtonAction={onClose}
      confirmButtonText="Close"
      confirmButtonAction={onClose}
      headerButtonAction={() => setIntegrationToEdit(null)}
      headerButtonText="Create Integration"
    >
      <MuiBox py="16px">
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer>
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      sx={{ ...tableHeaderSx, width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => onSortClick('name')}
                    >
                      Name
                      {renderSortIcon('name')}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      sx={{ ...tableHeaderSx, width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => onSortClick('code')}
                    >
                      Code
                      {renderSortIcon('code')}
                    </MuiTable.TableCell>
                    <MuiTable.TableCell sx={{ ...tableHeaderSx, textAlign: 'center' }}>Actions</MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {sortedIntegrations.map((integration: Integration) => (
                    <MuiTable.TableRow key={integration.id}>
                      <MuiTable.TableCell>{integration.name}</MuiTable.TableCell>
                      <MuiTable.TableCell>{integration.code}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        <MuiBox display="flex" gap="10px">
                          <Tooltip title="Edit Integration" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => setIntegrationToEdit(integration)}
                              data-testid="edit-integration-button"
                            >
                              <SvgIcon name="edit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          <Tooltip title="Copy Integration" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => onCopyClick(integration)}
                              data-testid="copy-integration-button"
                            >
                              <SvgIcon name="copy_circle" />
                            </SvgIconButton>
                          </Tooltip>
                          <Tooltip title="Delete Integration" arrow placement="top" verticalOffset={-8}>
                            <SvgIconButton
                              disabled={pending}
                              onClick={() => setIntegrationToDelete(integration)}
                              data-testid="delete-integration-button"
                            >
                              <SvgIcon name="exit_circle" />
                            </SvgIconButton>
                          </Tooltip>
                        </MuiBox>
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
      </MuiBox>
      {integrationToDelete && (
        <FormDialog
          open
          confirmButtonAction={onDeleteConfirmClick}
          confirmButtonText="Delete"
          cancelButtonAction={() => setIntegrationToDelete(null)}
          cancelButtonText="Cancel"
        >
          <Typography fontSize={24}>Are you sure you want to delete this integration?</Typography>
        </FormDialog>
      )}
      {integrationToEdit !== undefined && (
        <IntegrationFormOverlay
          integration={integrationToEdit ?? undefined}
          onClose={() => setIntegrationToEdit(undefined)}
          onSubmit={onFormSubmit}
        />
      )}
      {error && (
        <FormDialog cancelButtonAction={resetError} confirmButtonAction={resetError} confirmButtonText="Ok" open>
          <Typography fontSize={24} maxWidth="524px">
            {errorCode ? error : 'Uh oh! Something went wrong. Please try again.'}
          </Typography>
        </FormDialog>
      )}
    </FormDialog>
  );
};

export default ManageIntegrationsOverlay;
